import { Klydo } from "../../../api/klydo";
import { Device, InsertToMode } from "../../../Types";
import DSTypography from "../../system-design/DSTypography";
import DSBox from "../../system-design/DSBox";
import ButtonLoader from "../../../components/Widgets/ButtonLoader";
import CurrTime from "../../../components/Widgets/CurrTime";
import { NavLink } from "react-router-dom";
import { onlineCheck } from "../../../utils/util";
import KlydoListItem from "../../KlydoListItem";
import DSButton from "../../system-design/DSButton";
import DSTextField from "../../system-design/DSTextField";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import ErrorIcon from "@mui/icons-material/Error";
import CloseIcon from "@mui/icons-material/Close";
import DSIconButton from "../../system-design/DSIconButton";
import DSPaper from "../../system-design/DSPaper";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSBaseTable, {
  Column,
} from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import { DSTableCell } from "../../system-design/Table";
import DSChip from "../../system-design/DSChip";
import DSSwitch from "../../system-design/DSSwitch";
import DSFormControl from "../../system-design/DSFormControl";
import DSFormControlLabel from "../../system-design/DSFormControlLabel";
import DSInputLabel from "../../system-design/DSInputLabel";
import DSSelect from "../../system-design/DSSelect";
import DSMenuItem from "../../system-design/DSMenuItem";

import useSendKlydoModal from "../hooks/useSendKlydoModal";
import useExploreCategoriesQuery from "../../ExploreCategories/hooks/useExploreCategoriesQuery";

interface SendKlydoModalProps {
  open: boolean;
  handleClose: () => void;
  klydos: Klydo[];
  onlyDev?: boolean;
}

const INSERT_MODES: InsertToMode[] = ["FEED", "FAVORITE", "EXPLORE"];

function SendKlydoModal({
  open,
  handleClose,
  klydos,
  onlyDev,
}: SendKlydoModalProps) {
  const {
    selectedDevices,
    errorMessage,
    isSending,
    handleFreeSearch,
    currentData,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
    handleSendAndClose,
    toggleDeviceSelection,
    handleSelectAll,
    resetModalState,
    insertModes,
    toggleInsertMode,
    exploreCategoryTarget,
    setExploreCategoryTarget,
    ttlEnabled,
    setTtlEnabled,
    ttl,
    setTtl,
    scheduleEnabled,
    setScheduleEnabled,
    scheduledTime,
    setScheduledTime,
    playNow,
    setPlayNow,
    forceDownload,
    setForceDownload,
  } = useSendKlydoModal({ klydos, onlyDev });

  const { data: exploreCategories } = useExploreCategoriesQuery();

  const handleModalClose = () => {
    resetModalState();
    handleClose();
  };

  const isSendDisabled =
    selectedDevices.length === 0 ||
    insertModes.size === 0 ||
    (insertModes.has("EXPLORE") && !exploreCategoryTarget) ||
    // Clearing the number field yields Number("") === 0 — don't let a
    // 0-minute TTL through (the input's min=1 only blocks the spinner).
    (ttlEnabled && (!ttl || ttl < 1)) ||
    // insertAt 0 means "insert now" — scheduling with no time picked would
    // send immediately, the opposite of intent.
    (scheduleEnabled && !scheduledTime);

  const tableColumns: Column<Device>[] = [
    { field: "online" as keyof Device, label: "Online", size: 40 },
    { field: "idf" as keyof Device, label: "Device ID" },
    { field: "clientName" as keyof Device, label: "Client" },
    { field: "location" as keyof Device, label: "Location" },
    { field: "clockTime" as keyof Device, label: "Clock Time", size: 90 },
    { field: "currentIdf" as keyof Device, label: "Current Klydo" },
  ];

  const rowBuilder = (row: Device, columns: Column<Device>[]) => {
    return (
      <>
        {columns.map((column) => {
          let content: React.ReactNode;

          if (column.field === "online") {
            content = (
              <i
                className={`fa ${
                  row.premium && row.premium.length ? "fa-star" : "fa-circle"
                } ${
                  onlineCheck(new Date(row.heartbeat))
                    ? "text-success"
                    : "text-danger"
                }`}
              />
            );
          } else if (column.field === "idf") {
            content = <span title={row.idf}>{row.idf}</span>;
          } else if (column.field === "clientName") {
            content = row.clientName || "-no info-";
          } else if (column.field === "location") {
            const city = row.location?.city ? row.location.city : "no city";
            const country = row.location?.country_code
              ? row.location.country_code
              : "no country";
            content = `${city}, ${country}`;
          } else if (column.field === "clockTime") {
            content = row.clocktime ? (
              <CurrTime time={row.clocktime} offset={row.timeOffset?.value} />
            ) : (
              "-no info-"
            );
          } else if (column.field === "currentIdf") {
            content = row.klydo ? (
              <NavLink to="#" title={row.klydo.id}>
                {row.klydo.id}
              </NavLink>
            ) : (
              "-no klydo-"
            );
          } else {
            content = String(row[column.field]);
          }

          return (
            <DSTableCell
              key={column.field as string}
              sx={{
                width: column.size ? `${column.size}px` : undefined,
                whiteSpace: "nowrap",
              }}
            >
              {content}
            </DSTableCell>
          );
        })}
      </>
    );
  };

  return (
    <DSDialog open={open} onClose={handleModalClose} maxWidth="lg" fullWidth>
      <DSDialogTitle>
        Send Klydo to Devices
        <DSIconButton
          aria-label="close"
          onClick={handleModalClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </DSIconButton>
      </DSDialogTitle>

      <DSDialogContent>
        {errorMessage && (
          <DSPaper
            elevation={0}
            sx={{
              bgcolor: "#f8d7da",
              color: "#721c24",
              p: 2,
              mb: 2,
              borderRadius: 1,
              border: "1px solid #f5c6cb",
            }}
          >
            <DSBox display="flex" alignItems="center" mb={1}>
              <ErrorIcon color="error" sx={{ mr: 1 }} />
              <DSTypography variant="h6" color="error">
                Error
              </DSTypography>
            </DSBox>
            <DSTypography>{errorMessage}</DSTypography>
          </DSPaper>
        )}

        <DSBox mb={2}>
          <DSTypography variant="h6">
            Selected Klydos ({klydos.length})
          </DSTypography>
          {klydos.map((klydo) => (
            <DSBox key={klydo.id} sx={{ mb: 1 }}>
              <KlydoListItem klydoId={klydo.id} />
            </DSBox>
          ))}
        </DSBox>

        <DSPaper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <DSTypography variant="h6" sx={{ mb: 1 }}>
            Pending Klydo Settings
          </DSTypography>
          <DSTypography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            These settings apply to every selected Klydo on every selected
            device.
          </DSTypography>

          <DSBox sx={{ mb: 2 }}>
            <DSTypography variant="subtitle2" sx={{ mb: 1 }}>
              Insert To
            </DSTypography>
            {INSERT_MODES.map((mode) => (
              <DSFormControlLabel
                key={mode}
                control={
                  <DSSwitch
                    checked={insertModes.has(mode)}
                    onChange={() => toggleInsertMode(mode)}
                  />
                }
                label={mode}
              />
            ))}
          </DSBox>

          {insertModes.has("EXPLORE") && (
            <DSFormControl fullWidth size="small" sx={{ mb: 2 }}>
              <DSInputLabel>Explore Category</DSInputLabel>
              <DSSelect
                value={exploreCategoryTarget}
                label="Explore Category"
                onChange={(e) =>
                  setExploreCategoryTarget(e.target.value as string)
                }
              >
                {(exploreCategories ?? []).map((cat) => (
                  <DSMenuItem key={cat.id} value={cat.id}>
                    {cat.name || cat.id}
                  </DSMenuItem>
                ))}
              </DSSelect>
            </DSFormControl>
          )}

          <DSBox sx={{ mb: 1 }}>
            <DSFormControlLabel
              control={
                <DSSwitch
                  checked={ttlEnabled}
                  onChange={(e) => setTtlEnabled(e.target.checked)}
                />
              }
              label="Time to Live (TTL)"
            />
            {ttlEnabled && (
              <DSTextField
                type="number"
                size="small"
                label="TTL (minutes)"
                value={ttl}
                onChange={(e) => setTtl(Number(e.target.value))}
                inputProps={{ min: 1 }}
                sx={{ ml: 2, width: 150 }}
              />
            )}
          </DSBox>

          <DSBox sx={{ mb: 1 }}>
            <DSFormControlLabel
              control={
                <DSSwitch
                  checked={scheduleEnabled}
                  onChange={(e) => setScheduleEnabled(e.target.checked)}
                />
              }
              label="Schedule for later"
            />
            {scheduleEnabled && (
              <input
                type="datetime-local"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                style={{ marginLeft: 16, padding: "6px 8px" }}
              />
            )}
          </DSBox>

          <DSFormControlLabel
            control={
              <DSSwitch
                checked={playNow}
                onChange={(e) => setPlayNow(e.target.checked)}
              />
            }
            label="Play Now (switch to mode and play immediately)"
          />

          <DSFormControlLabel
            control={
              <DSSwitch
                checked={forceDownload}
                onChange={(e) => setForceDownload(e.target.checked)}
              />
            }
            label="Force Download (re-download all assets even if cached)"
          />
        </DSPaper>

        <DSBox>
          <DSTextField
            fullWidth
            placeholder="Device search"
            onChange={(e) => handleFreeSearch(e.target.value)}
            margin="normal"
            size="small"
          />

          <DSBox sx={{ height: "400px", overflow: "auto", mt: 2 }}>
            {currentData && !isLoading ? (
              <>
                <DSBaseTable
                  data={(currentData || []) as unknown as Device[]}
                  columns={tableColumns}
                  rowBuilder={rowBuilder}
                  selectedRows={selectedDevices}
                  onSelectRows={handleSelectAll}
                  fetchMore={fetchNextPage}
                  onSelectSingleRow={toggleDeviceSelection}
                />
                {isFetchingNextPage && (
                  <DSBox
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      py: 1,
                    }}
                  >
                    <DSCircularProgress size={24} />
                  </DSBox>
                )}
              </>
            ) : (
              <DSBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <DSCircularProgress />
              </DSBox>
            )}
          </DSBox>
        </DSBox>
      </DSDialogContent>

      <DSDialogActions>
        <DSBox sx={{ flexGrow: 1 }}>
          <DSTypography>
            {selectedDevices.length} device
            {selectedDevices.length !== 1 ? "s" : ""} selected
          </DSTypography>
          {selectedDevices.length > 0 && (
            <DSBox
              sx={{
                display: "flex",
                flexWrap: "wrap",
                mt: 1,
                maxHeight: "80px",
                overflow: "auto",
                gap: 0.5,
              }}
            >
              {selectedDevices.map((device) => (
                <DSChip
                  key={device.id}
                  label={device.idf || device.id}
                  size="small"
                  variant="outlined"
                  title={`${device.clientName || "Unknown"} - ${device.location?.city || "Unknown location"}`}
                />
              ))}
            </DSBox>
          )}
        </DSBox>
        <DSButton onClick={handleModalClose}>Cancel</DSButton>
        <ButtonLoader
          title="Send"
          disabled={isSendDisabled}
          loading={isSending}
          onClick={() => handleSendAndClose(handleModalClose)}
        />
      </DSDialogActions>
    </DSDialog>
  );
}

export default SendKlydoModal;
